import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useMarkCompleteV3 } from 'hooks/useMarkCompleteV3'
import confettiSelector from 'components/ConfettiButton/confettiSelector'
import Component from './component'
import { isFlagOn } from 'utils/featureFlags'
import theme from 'themes/variables'

/**
 * Connects the useMarkCompleteV3 hook to the presentational component.
 *
 * Props:
 *  coursePointer     - coursePointer from routeContent (GET /v1/content response)
 *  courseNodeId      - from page.page.metadata.resource_metadata
 *                      .course_node_connector.course_node_entry_point
 *  contentGroupCode  - curriculum-service content group code (e.g. 'dwb')
 *  instructorDataKey - top-level key in /v2/instructor/me response (e.g. 'Dwb')
 *  lessonSlug        - URL lesson segment (e.g. 'lesson-01') for DWB index-based matching
 *  siteId            - optional; resolved from userContext/URL if omitted
 */
function MarkCompleteV3Container({
  coursePointer,
  courseNodeId,
  contentGroupCode,
  instructorDataKey,
  lessonSlug,
  siteId,
  useSecondaryMarkCompleteButton = false,
}) {
  const {
    isComplete: isCompleteFromData,
    handleMarkComplete,
  } = useMarkCompleteV3({
    coursePointer,
    courseNodeId,
    contentGroupCode,
    instructorDataKey,
    lessonSlug,
    siteId,
  })

  const [optimisticComplete, setOptimisticComplete] = useState(false)

  useEffect(() => {
    if (isCompleteFromData) {
      setOptimisticComplete(false)
    }
  }, [isCompleteFromData])

  const isComplete = isCompleteFromData || optimisticComplete
  const confettiOptions = {
    colors: [
      theme.colors.saturatedPurple,
      theme.colors.elemDigital1,
      theme.colors.blueLighter,
      theme.colors.orangeLighter,
      theme.colors.yellowLighter,
    ],
  }
  const fireConfetti = confettiSelector('large', confettiOptions)

  const onMarkComplete = async event => {
    const button = event.currentTarget
    const success = await handleMarkComplete()
    if (success) {
      setOptimisticComplete(true)
      if (isFlagOn(['feature_LEARN-28848-add-confetti'])) {
        await fireConfetti({ currentTarget: button })
      }
    }
  }

  return (
    <Component
      handleMarkComplete={onMarkComplete}
      isComplete={isComplete}
      useSecondaryMarkCompleteButton={useSecondaryMarkCompleteButton}
    />
  )
}

MarkCompleteV3Container.propTypes = {
  contentGroupCode: PropTypes.string.isRequired,
  courseNodeId: PropTypes.string,
  coursePointer: PropTypes.shape({
    href: PropTypes.string,
    method: PropTypes.string,
    query: PropTypes.object,
  }),
  instructorDataKey: PropTypes.string.isRequired,
  lessonSlug: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  useSecondaryMarkCompleteButton: PropTypes.bool,
}

export default MarkCompleteV3Container
